import { useMemo } from 'react';

import { LoadingLayout } from 'components';
import { ConfirmationDialogContent } from 'components/dialogs/ConfirmationDialog/ConfirmationDialogContent';
import { useTranslation } from 'libs/i18n';
import { Dialog, DialogProps } from 'libs/ui';

import { Form, getSchema, Submit } from '../../forms/changeStatusForm';
import { useChangeStatus } from '../../hooks/useChangeStatus';
import { useOperationContext } from '../../providers/OperationProvider';

import { FormContent } from './FormContent';

interface ChangeStatusDialogContentFeatureProps {
  onClose: () => void;
  onSuccess: () => void;
}
interface DialogChangeStatusProps
  extends Pick<ChangeStatusDialogContentFeatureProps, 'onClose' | 'onSuccess'>,
    Omit<DialogProps, 'onClose'> {}

const ChangeStatusDialogContentFeature = ({
  onClose,
  onSuccess,
}: ChangeStatusDialogContentFeatureProps) => {
  const { t } = useTranslation();

  const { operation } = useOperationContext();

  const { loading, submitLoading, availableStatusesMap, onSubmit } =
    useChangeStatus({
      onSuccessCallback: onSuccess,
      operationId: operation.id,
      versionNumber: operation.versionNumber,
    });

  const schema = useMemo(
    () => getSchema({ availableStatusesMap }),
    [availableStatusesMap]
  );

  return (
    <Form validationSchema={schema} onSubmit={onSubmit}>
      <ConfirmationDialogContent
        headerCentered
        buttonConfirm={Submit}
        confirmButtonLabel={t('common.continue')}
        loading={submitLoading}
        subtitle={t('operation.operation.dialogStatusChange.subtitle')}
        title={t('operation.operation.dialogStatusChange.title')}
        onBack={onClose}
      >
        {loading ? (
          <LoadingLayout />
        ) : (
          <FormContent availableStatusesMap={availableStatusesMap} />
        )}
      </ConfirmationDialogContent>
    </Form>
  );
};

export const DialogChangeStatusFeature = ({
  isOpen,
  onClose,
  onSuccess,
}: DialogChangeStatusProps) => (
  <Dialog fullWidth isOpen={isOpen} maxWidth="sm" onClose={onClose}>
    <ChangeStatusDialogContentFeature onClose={onClose} onSuccess={onSuccess} />
  </Dialog>
);
